// components/portal/SourceShortlistPanel.tsx
'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isScored } from '#lib/leasing/leadStatus';
import { criterionTitle } from '#lib/leasing/criterionMatch';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { SourceBulkEnrichPanel } from './SourceBulkEnrichPanel';
import { SourceDoctrineModal } from './SourceDoctrineModal';
import { SourceFootfallPanel } from './SourceFootfallPanel';
import { PanelSection, PanelRow } from './panelSection';
import { BookOpen } from '@/components/ui/lucide-memo';
import {
  SOURCING_CAMPAIGN_DOCTRINE_MAX_LENGTH,
  type DoctrineSectionContentResponse,
  type ScoringCriterion,
  type SourcingCampaignDoctrineContextDTO,
} from '#types/types';
import { ACTIVE_SOURCING_CAMPAIGN_STATUSES } from '#lib/leasing/sourcingCampaignLifecycle';

interface SourceShortlistPanelProps {
  /** The open campaign whose shortlist is being inspected. */
  campaignId: string;
  /** Which sourcing scope the open campaign belongs to. */
  scope: 'portfolio' | 'asset';
  lang: string;
  /** The run is still filling the shortlist, so the figures below are partial. */
  stillRunning?: boolean;
}

// Cap the pool we inspect/act on. A shortlist can be large; rows can still be selected in the
// table to target a subset.
const SHORTLIST_FETCH_LIMIT = 300;

/**
 * The right bar in an open shortlist: a TECHNICAL BAR, in the inspector sense. It describes
 * whatever is currently in scope and offers the actions that apply to it.
 *
 *   - rows selected in the table -> the selection is the scope (SourceSetupPanel routes there)
 *   - nothing selected           -> the active subject is the scope: the shortlist itself
 *
 * It does not narrate the run. Agent activity is telemetry and belongs in the left chat.
 */
/** Stops a live run. Shared so it stays on screen whatever the right bar shows (owner, 2026-09-28). */
export const StopRunButton = ({ campaignId, lang }: { campaignId: string; lang: string }) => {
  const { kernel } = useKernel();
  const [isStopping, setIsStopping] = useState(false);
  return (
    <button
      type="button"
      disabled={isStopping}
      onClick={async () => {
        setIsStopping(true);
        try {
          await kernel.current?.stopSourcingCampaign(campaignId);
        } finally {
          setIsStopping(false);
        }
      }}
      className="mt-3 h-9 w-full rounded-full border border-[#D9534F]/30 bg-[#D9534F]/10 px-3 text-[#E88C89] transition-colors hover:bg-[#D9534F]/20 disabled:opacity-50"
      style={{ fontSize: 13 }}
    >
      {isStopping
        ? (lang === 'fr' ? 'Arrêt…' : 'Stopping…')
        : (lang === 'fr' ? 'Arrêter la recherche' : 'Stop the run')}
    </button>
  );
};

export const SourceShortlistPanel = ({ campaignId, scope, lang, stillRunning }: SourceShortlistPanelProps) => {
  const { kernel } = useKernel();
  const [fitIds, setFitIds] = useState<string[]>([]);
  const leadRegistry = useKernelStore((state) => state.inventory.Lead.registry);
  const portfolioFitRegistry = useKernelStore((state) => state.inventory.PortfolioFit.registry);
  const showDismissed = useKernelStore((state) => state.sourceShowDismissed);
  // Counted off the rows this panel already holds, so the control cannot claim a number the table
  // does not have.
  const dismissedCount = useMemo(
    () =>
      fitIds.filter((id) => {
        const row = (scope === 'portfolio' ? portfolioFitRegistry : leadRegistry)[id] as
          { status?: string | null } | undefined;
        return String(row?.status ?? '').toUpperCase() === 'DISMISSED';
      }).length,
    [fitIds, leadRegistry, portfolioFitRegistry, scope],
  );

  const loadShortlist = useCallback(async (): Promise<string[]> => {
    const k = kernel.current;
    const trimmed = typeof campaignId === 'string' ? campaignId.trim() : '';
    if (!k || !trimmed) return [];
    // Fit entity + campaign filter field differ by scope (mirrors the leads/fits tables:
    // asset Leads filter on lastCampaignId, unit/portfolio fits on campaignId).
    const entity = scope === 'portfolio' ? 'PortfolioFit' : 'Lead';
    const sortBy = scope === 'asset' ? 'fitScore' : 'score';
    // Entity (and therefore the filter shape) is resolved at runtime, so the static filter
    // union cannot be narrowed here, type as any for the dynamic query.
    const filters: any =
      scope === 'asset'
        ? { lastCampaignId: trimmed, sortBy, sortDir: 'desc' }
        : { campaignId: trimmed, sortBy, sortDir: 'desc' };
    const result = await k.queryInventory(entity, {
      filters,
      limit: SHORTLIST_FETCH_LIMIT,
      offset: 0,
      force: true,
    });
    return Array.isArray(result?.ids) ? result.ids : [];
  }, [kernel, campaignId, scope]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const ids = await loadShortlist();
        if (!cancelled) setFitIds(ids);
      } catch {
        if (!cancelled) setFitIds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-read while the run is filling so the figures track it.
  }, [loadShortlist, stillRunning]);

  // What the shortlist actually contains, read off the hydrated rows.
  const stats = useMemo(() => {
    const registry = scope === 'portfolio' ? portfolioFitRegistry : leadRegistry;
    const scoreKey = scope === 'asset' ? 'fitScore' : 'score';
    let scored = 0;
    let top: number | null = null;
    let low: number | null = null;
    let sum = 0;
    const brands = new Set<string>();
    for (const id of fitIds) {
      const row = registry[id] as Record<string, unknown> | undefined;
      if (!row) continue;
      const brandId = typeof row.brandId === 'string' ? row.brandId : null;
      if (brandId) brands.add(brandId);
      // A run that ERRORED persists fitScore 0 with a "System Error" rationale, and the table
      // renders its score cell as "-". The stats counted it as a real zero, so one failed lead
      // dragged "Lowest score" to 0 on a shortlist whose worst genuine score was 32, and pulled
      // the average down with it. A row that never produced a score is not a score of zero.
      const rowStatus = typeof row.status === 'string' ? row.status : '';
      const raw = row[scoreKey];
      if (isScored(rowStatus) && typeof raw === 'number' && Number.isFinite(raw)) {
        scored += 1;
        sum += raw;
        top = top === null ? raw : Math.max(top, raw);
        low = low === null ? raw : Math.min(low, raw);
      }
    }
    return {
      rows: fitIds.length,
      brands: brands.size,
      brandIds: Array.from(brands),
      top,
      low,
      average: scored > 0 ? Math.round(sum / scored) : null,
    };
  }, [fitIds, scope, leadRegistry, portfolioFitRegistry]);

  // What the rows ARE, in the reader's language.
  const rowLabel = lang === 'fr' ? 'Marques' : 'Brands';

  return (
    <div className="custom-scrollbar flex h-full w-full flex-col overflow-y-auto px-3 py-3">
      {/* What is in scope, on the Launch bar's card (owner, 2026-09-28). */}
      <div className="rounded-[20px] border border-[#262626] bg-[#1B1B1B] p-4">
        <div className="font-medium text-white" style={{ fontSize: 15 }}>
          {lang === 'fr' ? 'Liste retenue' : 'Shortlist'}
        </div>
        <div className="mt-1 text-[#8E8E8E]" style={{ fontSize: 13 }}>
          {stillRunning
            ? lang === 'fr'
              ? 'Source travaille, ces chiffres évoluent.'
              : 'Source is working, these figures are still moving.'
            : lang === 'fr'
              ? 'Sélectionnez des lignes pour cibler une partie.'
              : 'Select rows to target part of it.'}
        </div>
        {/* SHOW WHAT WAS TURNED DOWN. A dismissed row is a decision, not a failure, and hiding it
            by default keeps the list readable - but an extend that adds three brands and dismisses
            two then looks like it added one, which is exactly how "I don't even see the new brands"
            happens. The count is on the control so the reader knows there is something behind it. */}
        {dismissedCount > 0 ? (
          <button
            type="button"
            onClick={() => kernel.current?.setState({ sourceShowDismissed: !showDismissed })}
            className="mt-3 h-9 w-full rounded-full border border-[#2A2A2A] px-3 text-[#E5E5E5] transition-colors hover:bg-white/[0.06]"
            style={{ fontSize: 13 }}
          >
            {showDismissed
              ? (lang === 'fr' ? `Masquer les ${dismissedCount} ecartees` : `Hide the ${dismissedCount} dismissed`)
              : (lang === 'fr' ? `Voir les ${dismissedCount} ecartees` : `Show the ${dismissedCount} dismissed`)}
          </button>
        ) : null}
        {/* STOP. A run spends a credit per brand, and until now the only way to halt one was to
            ASK the agent to - which is no use at all when the agent is the thing that started it.
            A launch nobody asked for is recoverable only if the person watching it can stop it
            themselves, so the control lives here, next to the words saying a run is live.
            Stopping keeps every brand already scored; it only prevents the rest being billed. */}
        {stillRunning ? <StopRunButton campaignId={campaignId} lang={lang} /> : null}
      </div>

      {/* Its elements */}
      <dl className="mt-4 flex flex-col">
        <PanelRow label={rowLabel} value={String(stats.rows)} />
        {stats.average !== null ? (
          <PanelRow label={lang === 'fr' ? 'Score moyen' : 'Average score'} value={String(stats.average)} />
        ) : null}
        {stats.top !== null ? (
          <PanelRow label={lang === 'fr' ? 'Meilleur score' : 'Top score'} value={String(stats.top)} />
        ) : null}
        {stats.low !== null ? (
          <PanelRow label={lang === 'fr' ? 'Score le plus bas' : 'Lowest score'} value={String(stats.low)} />
        ) : null}
      </dl>

      {/* Its doctrine: what this shortlist scores on, and how it is actually scoring. The portfolio
          scope stays out: a PortfolioFitCampaign has neither a rubric nor a brief. */}
      {scope === 'asset' ? (
        <ShortlistDoctrine
          // Remount on campaign change: a new shortlist is a new doctrine, so none of the state
          // below should survive the switch.
          key={campaignId}
          campaignId={campaignId}
          scope={scope}
          lang={lang}
          stillRunning={stillRunning}
        />
      ) : null}

      {scope === 'asset' ? <SourceFootfallPanel key={`footfall:${campaignId}`} campaignId={campaignId} lang={lang} /> : null}

      {/* What can be done to them */}
      <SourceBulkEnrichPanel fitIds={fitIds} lang={lang} mode="shortlist" embedded />
    </div>
  );
};

SourceShortlistPanel.displayName = 'SourceShortlistPanel';

// ─────────────────────────────────────────────────────────────────────────────────────────
// The shortlist's own doctrine.
//
// There are exactly TWO objects: the INSTRUCTIONS (free prose, which is where any hypothesis
// lives) and the SCORING RUBRIC. Both are edited in one place, the doctrine workspace
// (SourceDoctrineModal), and both are editable by the agent from the chat. This panel does not
// edit anything: it REFLECTS them, live, and opens the workspace.
//
// Why this component talks to /api/* directly, against the usual inventory rule (AGENTS.md 4.1):
// doctrine is NOT an inventory entity. It has no kernel registry, no DTO in the inventory
// registry and no socket stream; the established precedent is components/portal/DoctrineView.tsx,
// which reads and writes /api/doctrine directly. The read-only doctrine-context endpoint is
// doctrine-adjacent (it describes the rubric and the scoring of one shortlist, not inventory rows)
// and follows the same precedent.
//
// How an agent edit reaches this panel without a refresh, using the mechanisms already in place:
//   - the RUBRIC is a Property field, so the agent's MUTATE rides the normal inventory path
//     (socket -> serializeEntityForEvent -> Zustand Property registry). We subscribe to that
//     registry and re-read the scoring context whenever the asset's rubric changes.
//   - the INSTRUCTIONS are a DoctrineDocument, which has no socket stream, so the chat command
//     that writes them bumps `sourceDoctrineRefreshToken` in the kernel store, exactly the way a
//     campaign launch bumps `sourceCampaignsRefreshToken` for the dashboard.
// ─────────────────────────────────────────────────────────────────────────────────────────

interface ShortlistDoctrineProps {
  /** Brands on this shortlist, for the paid re-analysis path. */
  campaignId: string;
  /** Which grid governs this run, and therefore which asset field the editor writes. */
  scope: 'asset';
  lang: string;
  stillRunning?: boolean;
  /** With a brand open the bar keeps the doctrine's door only: the shortlist's grid is not about
   *  that brand, and listing it read as the scoring shown twice (owner, 2026-10-03). */
  doctrineOnly?: boolean;
}

// Statuses where the shortlist is still being filled, so its aggregates are still moving.
const ACTIVE_CAMPAIGN_STATUSES = new Set<string>(ACTIVE_SOURCING_CAMPAIGN_STATUSES);

// How long the doctrine workspace waits after the last keystroke before it persists. There is no
// apply button anywhere in this flow: an edit IS a save, it just does not fire per character.
const DOCTRINE_SAVE_DEBOUNCE_MS = 700;

/**
 * French rubric labels are written as "Head: the long explanation of what is judged", so the
 * head alone is the readable name in a narrow right bar. Re-exported because the selected-brand
 * panel lists the SAME criteria and must read identically, not almost identically, and now so
 * does the lead scoring table, which is why the implementation moved to criterionMatch.
 */
export const labelHead = criterionTitle;

/**
 * A shape-only fingerprint of a rubric. Used to tell "the asset's rubric changed under us" from
 * "this is the value we just wrote": a round trip through jsonb reorders object keys, so
 * JSON.stringify cannot answer that question and this can.
 */
const rubricStamp = (rubric: unknown): string =>
  Array.isArray(rubric)
    ? rubric
        .map((raw) => {
          const criterion = (raw ?? {}) as Partial<ScoringCriterion>;
          return `${criterion.id ?? ''}|${criterion.label ?? ''}|${criterion.weight ?? ''}|${criterion.minimumScore ?? ''}`;
        })
        .join('~')
    : '';

export const ShortlistDoctrine = ({ campaignId, scope, lang, stillRunning, doctrineOnly = false }: ShortlistDoctrineProps) => {
  const { kernel } = useKernel();
  const fr = lang === 'fr';
  const [context, setContext] = useState<SourcingCampaignDoctrineContextDTO | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [doctrineOpen, setDoctrineOpen] = useState(false);

  const doctrineKey = `campaign:${campaignId}`;
  // A run that is still going keeps moving the aggregates, and the prop can lag behind the
  // campaign row, so trust whichever of the two says the run is active.
  const runActive = Boolean(stillRunning) || ACTIVE_CAMPAIGN_STATUSES.has(context?.campaignStatus ?? '');
  const propertyId = context?.propertyId ?? '';

  // The asset's stored rubric for THIS granularity, straight off the inventory registry. This is
  // the subscription that makes an agent MUTATE show up here: the socket update replaces the
  // Property block in the Zustand store, this selector returns a different stamp, and the effect
  // below re-reads.
  const rubricField = 'scoringRubric';
  const storedRubric = useKernelStore((state) =>
    propertyId
      ? ((state.inventory.Property.registry[propertyId] as Record<string, unknown> | undefined)?.[rubricField] ?? null)
      : null,
  );
  const storedRubricStamp = rubricStamp(storedRubric);
  // Bumped by the chat command that writes this shortlist's instructions (and by our own saves),
  // because a DoctrineDocument has no socket stream to subscribe to.
  const doctrineRefreshToken = useKernelStore((state) => state.sourceDoctrineRefreshToken);

  const loadContext = useCallback(async () => {
    const res = await fetch(
      `/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}/doctrine-context`,
      { cache: 'no-store' },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(
        (body && typeof body.message === 'string' && body.message) ||
          (fr ? 'Contexte de scoring indisponible.' : 'Scoring context unavailable.'),
      );
    }
    return (await res.json()) as SourcingCampaignDoctrineContextDTO;
  }, [campaignId, fr]);

  // First read. The component is keyed on campaignId by its parent, so there is no stale state to
  // reset here. `loaded` matters: until it flips, the sections below must say "loading" and never
  // "no criterion", or a slow or refused read reads exactly like an asset with no rubric.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const scoring = await loadContext();
        if (!cancelled) setContext(scoring);
      } catch (error) {
        if (!cancelled) setContextError(error instanceof Error ? error.message : String(error));
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadContext]);

  // Re-read on every event that can change what this panel shows, with no manual refresh:
  //   - the run stops (the aggregates settle, and a pass that ended short must be visible)
  //   - the asset's rubric changed (agent MUTATE, arriving over the inventory socket)
  //   - the shortlist's instructions were rewritten from the chat
  const wasActiveRef = useRef(false);
  const baselineRef = useRef<{ rubric: string; token: number } | null>(null);
  useEffect(() => {
    const runJustEnded = wasActiveRef.current && !runActive;
    wasActiveRef.current = runActive;
    if (!loaded) return;
    const baseline = baselineRef.current;
    baselineRef.current = { rubric: storedRubricStamp, token: doctrineRefreshToken };
    // First pass after the initial read: record what we are looking at, compare from now on.
    if (!baseline) return;
    if (!runJustEnded && baseline.rubric === storedRubricStamp && baseline.token === doctrineRefreshToken) {
      return;
    }
    void loadContext()
      .then((next) => {
        setContext(next);
        setContextError(null);
      })
      .catch(() => undefined);
  }, [runActive, storedRubricStamp, doctrineRefreshToken, loaded, loadContext]);

  const rubric = context?.effectiveRubric ?? [];
  // The rubric was not declared anywhere: it was reconstructed from what the rows were actually
  // scored on. Saying so is the difference between "here is the doctrine" and "here is what the
  // agent did", and the manager needs to know which one they are reading.
  const rubricObserved = context?.rubricOrigin === 'OBSERVED';

  return (
    <div className="mt-4 flex flex-col gap-3">
      {/* ITS DOCTRINE: one row, opened by the same BookOpen mark the nav uses for Doctrine. */}
      <PanelSection
        title="Doctrine"
        aside={
          <button
            type="button"
            onClick={() => setDoctrineOpen(true)}
            aria-label={fr ? 'Ouvrir la doctrine' : 'Open the doctrine'}
            title={fr ? 'Ouvrir la doctrine' : 'Open the doctrine'}
            className="flex h-8 w-8 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white"
          >
            <BookOpen size={16} />
          </button>
        }
      />

      {/* ITS SCORING: the one grid, with this run's weights. */}
      {doctrineOnly ? null : (<>
      <PanelSection
        title={fr ? 'Notation' : 'Scoring'}
        aside={rubric.length > 0 ? `${rubric.reduce((total, criterion) => total + criterion.weight, 0)} pts` : null}
      >
        {contextError ? (
          <div className="mt-2 text-[#C4705F]" style={{ fontSize: 11 }}>
            {contextError}
          </div>
        ) : !loaded ? (
          <div className="mt-2 text-[#6E6E6E]" style={{ fontSize: 11 }}>
            {fr ? 'Chargement…' : 'Loading...'}
          </div>
        ) : rubric.length === 0 ? (
          <div className="mt-2 text-[#6E6E6E]" style={{ fontSize: 11 }}>
            {fr ? 'Aucun critère de scoring sur cet actif.' : 'No scoring criterion on this asset.'}
          </div>
        ) : (
          <>
            {rubricObserved ? (
              <div className="mt-1.5 text-[#C09A5E]" style={{ fontSize: 10.5 }}>
                {fr
                  ? "Cet actif n'a plus de barème enregistré : ces critères sont ceux sur lesquels les lignes ont réellement été notées."
                  : 'This asset carries no stored rubric any more: these criteria are the ones the rows were actually scored on.'}
              </div>
            ) : null}
            <dl className="mt-1.5 flex flex-col">
              {rubric.map((criterion) => (
                <PanelRow
                  key={criterion.id}
                  label={labelHead(criterion.label)}
                  labelTitle={criterion.label}
                  value={String(criterion.weight)}
                />
              ))}
            </dl>
            {/* The run's own weights (lib/leasing/runWeights.ts): why they are what they are. */}
            {context?.runWeights ? (
              <div className="mt-1.5 text-[#8E8E8E]" style={{ fontSize: 12 }}>
                {context.runWeights.source === 'user'
                  ? (fr ? 'Pondération choisie pour ce sourcing' : 'Weights set for this run')
                  : (fr ? 'Pondération adaptée à ce sourcing' : 'Weights adapted to this run')}
                {context.runWeights.reason ? `${fr ? ' : ' : ': '}${context.runWeights.reason}` : ''}
              </div>
            ) : null}
          </>
        )}
      </PanelSection>
      </>)}

      <ShortlistDoctrineWorkspace
        open={doctrineOpen}
        onClose={() => setDoctrineOpen(false)}
        doctrineKey={doctrineKey}
        scope={scope}
        rubric={rubric}
        lang={lang}
      />
    </div>
  );
};

ShortlistDoctrine.displayName = 'ShortlistDoctrine';

// ─────────────────────────────────────────────────────────────────────────────────────────
// The doctrine workspace, bound to one shortlist.
//
// It is the EXISTING editor (SourceDoctrineModal): the ".csv" grid, read-only (the one grid of every
// run, lib/leasing/briefCriteria.ts), and the ".md" instructions page, backed by the DoctrineDocument
// `campaign:<campaignId>` via /api/doctrine (GET/PUT) and autosaved on a debounce.
// ─────────────────────────────────────────────────────────────────────────────────────────

interface ShortlistDoctrineWorkspaceProps {
  open: boolean;
  onClose: () => void;
  doctrineKey: string;
  scope: 'asset';
  /** The grid this run is scored on, with its weights. */
  rubric: ScoringCriterion[];
  lang: string;
}

const ShortlistDoctrineWorkspace = ({
  open,
  onClose,
  doctrineKey,
  scope,
  rubric,
  lang,
}: ShortlistDoctrineWorkspaceProps) => {
  const fr = lang === 'fr';
  const [instructions, setInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);
  // What the server last told us the instructions are. An incoming version is adopted only when
  // the user has nothing unsaved, so an agent edit lands live without eating what is being typed.
  const savedInstructionsRef = useRef('');
  const doctrineRefreshToken = useKernelStore((state) => state.sourceDoctrineRefreshToken);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/doctrine?key=${encodeURIComponent(doctrineKey)}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(String(res.status));
        const payload = (await res.json()) as DoctrineSectionContentResponse;
        const content = payload.document?.content ?? '';
        if (cancelled) return;
        setInstructions((current) => (current === savedInstructionsRef.current ? content : current));
        savedInstructionsRef.current = content;
      } catch {
        // A shortlist with no instructions yet reads as empty, which is the truth.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doctrineKey, doctrineRefreshToken]);

  // Autosave the instructions. The cap is checked before the write, not after: the document rides
  // into every future run, so publishing a text the server would refuse is worse than refusing it
  // here.
  useEffect(() => {
    if (instructions === savedInstructionsRef.current) return;
    if (instructions.length > SOURCING_CAMPAIGN_DOCTRINE_MAX_LENGTH) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
      setError(
        fr
          ? `Instructions trop longues : ${instructions.length} caractères pour un maximum de ${SOURCING_CAMPAIGN_DOCTRINE_MAX_LENGTH}.`
          : `Instructions too long: ${instructions.length} characters for a maximum of ${SOURCING_CAMPAIGN_DOCTRINE_MAX_LENGTH}.`,
      );
      return;
    }
    const pending = instructions;
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch('/api/doctrine', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: doctrineKey, content: pending }),
          });
          if (!res.ok) {
            const body = await res.json().catch(() => null);
            throw new Error(
              (body && typeof body.message === 'string' && body.message) ||
                (fr ? "Les instructions n'ont pas pu être enregistrées." : 'The instructions could not be saved.'),
            );
          }
          savedInstructionsRef.current = pending;
          setError(null);
        } catch (saveError) {
          setError(saveError instanceof Error ? saveError.message : String(saveError));
        }
      })();
    }, DOCTRINE_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [instructions, doctrineKey, fr]);

  return (
    <>
      <SourceDoctrineModal
        open={open}
        onClose={onClose}
        scope={scope}
        lang={lang}
        rubric={rubric}
        instructions={instructions}
        onInstructionsChange={setInstructions}
      />
      {open && error ? (
        <div
          className="fixed bottom-6 left-1/2 z-[130] -translate-x-1/2 rounded-[8px] border border-[#5A3A34] bg-[#221A18] px-3 py-2 text-[#C4705F]"
          style={{ fontSize: 11 }}
        >
          {error}
        </div>
      ) : null}
    </>
  );
};

ShortlistDoctrineWorkspace.displayName = 'ShortlistDoctrineWorkspace';
